<template>
  <div>

    <div v-if="!show404 && !show403" class="sx-page pp-v2">
      <!-- One page for the process and its activities (US 9369 + 9371): the hero, then the process first and its activities -->
      <ProcessHero v-if="process.id"
                   :process="process"
                   :row="switcherRow"
                   :history-on="tab === 'history'"
                   @edit="$refs.basicInfo.openEdit()"
                   @delete="$refs.basicInfo.openDelete()"
                   @history="openTab(tab === 'history' ? 'basic-info' : 'history')"
                   @updated="updateProcess"></ProcessHero>
      <V2PageLoading v-else part="hero"></V2PageLoading>
      <ProcessSwitcher ref="switcher" :process="$route.params.slug" @loaded="switcherLoaded" @changed="updateProcess"></ProcessSwitcher>

      <!-- The process's edit and delete dialogs, and an open update task; the hero shows the rest -->
      <BasicInfoBlock v-if="process.id" ref="basicInfo" :object="process" @updated="updateProcess" :edit="edit" hideHeader></BasicInfoBlock>

      <!-- Our own tab bar, as on the system and third-party pages (US 9370, 9371). History opens from the hero. -->
      <nav v-if="process.id" class="sx-tabs mt-3" role="tablist" qid="process-tabs">
        <button v-for="item in tabItems"
                :key="item.key"
                type="button"
                role="tab"
                class="sx-tabs__tab"
                :class="{ on: tab === item.key }"
                :aria-selected="tab === item.key ? 'true' : 'false'"
                :qid="'process-tab-' + item.key"
                @click="openTab(item.key)">
          <font-awesome-icon :icon="item.icon"/>{{ item.label }}
        </button>
      </nav>
      <V2PageLoading v-else part="tabs"></V2PageLoading>

      <div v-if="process.id" class="sx-tabs__content">
        <!-- Basic info -->
        <div v-show="tab === 'basic-info'" role="tabpanel" qid="process-tab-basic-info">
          <div class="card mt-3" qid="basic-info-section">
            <DescriptionBlock :object="process" @updated="updateProcess"></DescriptionBlock>
          </div>
          <PoliciesAndProceduresTable class="mt-3" :object="process" @updated="updateProcess" v-if="getClient.policyModule"></PoliciesAndProceduresTable>
        </div>

        <!-- GDPR, loaded the first time it's opened -->
        <div v-if="opened.gdpr && getClient.gdprModule && process.privacy_feature" v-show="tab === 'gdpr'" role="tabpanel" class="pp-stack" qid="process-tab-gdpr">
          <div class="card" qid="basic-info-section">
            <PurposeBlock :object="process" @updated="updateProcess"></PurposeBlock>
          </div>
          <template v-if="getClient.gdprAdvancedModule">
            <LegalBasisTable :object="process" @updated="updateProcess"></LegalBasisTable>
            <InformationObligationTable :object="process" @updated="updateProcess"></InformationObligationTable>
            <Riskprofile :object="process" @updated="updateProcess"></Riskprofile>
            <Confidentiality :object="process" @updated="updateProcess"></Confidentiality>
            <Integrity :object="process" @updated="updateProcess"></Integrity>
            <Availability :object="process" @updated="updateProcess"></Availability>
          </template>
        </div>

        <!-- Risk scenarios: the process's own and its activities' in one tab (US 9369 + 9371), fresh every time it's opened -->
        <div v-if="tab === 'risk-management' && getClient.riskModule" role="tabpanel" class="mt-3" qid="process-tab-risk-management">
          <AssetRiskTab asset="processes" catalogue-type="process" :object="process"
              :can-create="isGranted([permissions.PROCESS_RISK_SCENARIO_CREATE, permissions.PROCESS_RISK_SCENARIO_CREATE_OWN], process) && (!getClient.parent || process.partition)"
              :can-edit="isGranted([permissions.PROCESS_RISK_SCENARIO_EDIT, permissions.PROCESS_RISK_SCENARIO_EDIT_OWN], process) && (!getClient.parent || process.partition)"
              :can-delete="isGranted([permissions.PROCESS_RISK_SCENARIO_DELETE, permissions.PROCESS_RISK_SCENARIO_DELETE_OWN], process) && (!getClient.parent || process.partition)"
              :sources="switcherRow ? riskSources : null"
              @updated="updateProcess"></AssetRiskTab>
        </div>

        <!-- Assessments of the process and its activities in one tab (US 9369 + 9371) -->
        <div v-if="tab === 'assessments' && getClient.assessmentModule && process.assessment_feature" role="tabpanel" class="mt-3" qid="process-tab-assessments">
          <ProcessAssessmentsTab v-if="switcherRow" :process="process"
              :can-start="!!getClient.projectModule && isGranted(permissions.PROJECT_CREATE)"
              @start="$refs.createProject.showGoal('risk')"></ProcessAssessmentsTab>
          <AssessmentsTable v-else :process="process" @updated="updateProcess"></AssessmentsTable>
        </div>

        <!-- History, from the hero's button -->
        <div v-if="tab === 'history'" role="tabpanel" class="mt-3" qid="process-tab-history">
          <ProcessHistory :object="process"></ProcessHistory>
        </div>
      </div>
      <V2PageLoading v-else part="cards"></V2PageLoading>

      <!-- Opens the project launcher on a risk assessment (US 9369 + 9371) -->
      <span class="pm-hidden-trigger">
        <CreateProjectModal v-if="getClient.projectModule && process.id" ref="createProject" type="risk_assessment" :types="['risk_assessment']"></CreateProjectModal>
      </span>
    </div>


    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>


  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import ProcessEditForm from '@/views/Processes/ProcessEditForm.vue';
import Avatar from '@/components/Avatar.vue';
import ValidationList from '@/views/Validations/ValidationList.vue';
import ValidationForm from '@/views/Validations/ValidationForm.vue';
import PoliciesAndProceduresTable from '@/views/Processes/PoliciesAndProceduresTable.vue';
import LegalBasisTable from '@/views/Processes/LegalBasisTable.vue';
import InformationObligationTable from '@/views/Processes/InformationObligationTable.vue';
import BasicInfoBlock from '@/views/Processes/BasicInfoBlock.vue';
import ProcessSwitcher from '@/components/processes/ProcessSwitcher.vue';
import ProcessHero from '@/components/processes/ProcessHero.vue';
import {processRiskSources} from '@/components/processes/riskSources.js';
import ProcessAssessmentsTab from '@/components/processes/ProcessAssessmentsTab.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import DescriptionBlock from '@/views/Processes/DescriptionBlock.vue';
import PurposeBlock from '@/views/Processes/PurposeBlock.vue';
import Confidentiality from '@/views/Processes/Confidentiality.vue';
import Riskprofile from '@/views/Processes/Riskprofile.vue';
import Integrity from '@/views/Processes/Integrity.vue';
import Availability from '@/views/Processes/Availability.vue';
import V2PageLoading from '@/components/V2PageLoading.vue';
import {v2ModalsOn, v2ModalsOff} from '@/util/v2Modals.js';
import ProcessHistory from '@/views/Processes/ProcessHistory.vue';
import AssetRiskTab from '@/components/risk/AssetRiskTab.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import AssessmentsTable from '@/views/Processes/AssessmentsTable.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';


export default {
  name: 'ProcessView',
  data: function() {
    return {
      switcherRow: null,
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('processes.title'),
          to: '/processing-activities'
        }
      ],
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      showFullPurpose: false,
      process: {},
      edit: this.$route.params.edit,
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,
      lbValidationShow: false,

      itemToDelete: {},
      processGroups: [],
      tags: [],
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      selectedArticle: [],
      articles: [],
      selectedFiles: [],
      showArticleDescription: false,
      selectedLB: {},
      viewLBModal: false,
      editLBModal: false,
      updateValidationList: false,
      selectedLBSort: '',
      selectedLBDirection: '',
      showDescriptionInput: false,
      showPurposeInput: false,
      processEditDescription: '',
      processEditPurpose: '',
      // The open tab, and the tabs opened once (they stay loaded)
      tab: 'basic-info',
      opened: { 'basic-info': true },
      buttonDisabled: false,
      myTasks: [],
      myGuides: [],
      tabIndex: 0,
      selectedItem: {},
      showTagsModal: false,
      show404: false,
      show403: false,
    }
  },
  computed: {
    // The process's own risk scenarios and its activities' in one tab (US 9369 + 9371)
    riskSources() {
      return processRiskSources(this, this.process, this.switcherRow, null);
    },
    tabItems() {
      let client = this.getClient;
      return [
        { key: 'basic-info', icon: 'th-large', label: this.$t('processes.tab.basic_info') },
        { key: 'gdpr', icon: 'user-shield', label: this.$t('processes.tab.gdpr'), module: !!(client.gdprModule && this.process.privacy_feature) },
        { key: 'risk-management', icon: 'exclamation-triangle', label: this.$t('activities.tab.risk_management'), module: !!client.riskModule },
        { key: 'assessments', icon: 'chart-line', label: this.$t('systems.tab.assessments'), module: !!(client.assessmentModule && this.process.assessment_feature) }
      ].filter(item => item.module === undefined || item.module);
    },
    getClient() {
      return this.$store.state.client
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadPage() {
      return this.$store.state.updatePage
    },
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        let self = this;
        setTimeout(function(){
          if (self.$route.name == 'processes.view') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent(true);
      }
    },
    getReloadContent: function () {
      if (!this.showOnTask) {
        if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
          if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
            this.process = this.$store.state.updatedObject
          }
        }
      }
    },
    getReloadPage: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id && this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
        this.$router.push({ name: 'processes.view', params: { slug: this.$store.state.updatedObject.slug }, query: { task: this.$route.query.task }})
      } else {
        this.$router.push({ name: 'processes.view', params: { slug: this.process.slug }, query: { task: this.$route.query.task }})
      }
    },
    getTabIndex: function (newValue, oldValue) {

      this.handleUpdateTab(newValue)
    }
  },
  components: {
    ProcessSwitcher,
    ProcessHero,
    ProcessAssessmentsTab,
    CreateProjectModal,
    ProcessEditForm,
    Avatar,
    ValidationList,
    ValidationForm,
    Integrity,
    Availability,
    Confidentiality,
    Riskprofile,
    V2PageLoading,
    ProcessHistory,
    AssetRiskTab,
    ShowMoreModal,
    AssessmentsTable,
    DescriptionBlock,
    PurposeBlock,
    PoliciesAndProceduresTable,
    LegalBasisTable,
    InformationObligationTable,
    BasicInfoBlock,
    page403,
    page404,
  },
  methods: {
    // History opens from the hero, the other tabs from our tab bar
    openTab(key) {
      if (key === 'risk_management') {
        key = 'risk-management';
      }
      if (key !== 'history' && !this.tabItems.some(item => item.key === key)) {
        return;
      }
      this.tab = key;
      this.$set(this.opened, key, true);
    },
    goToList() {
      this.$router.push('/processing-activities')
    },
    openTagsModal(object) {
      this.selectedItem = object
      this.showTagsModal = true
    },
    closeTagsModal() {
      this.selectedItem = {}
      this.showTagsModal = false
    },
    getReducedTags(tags) {
      let reducedTags = []
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    openToolbar(object) {
      this.$store.commit('storeTaskToolbarState', true);
      this.$store.commit('storeActiveTask', object);
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Process' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Process',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    openHelpCenter(object) {
      let topElement = document.getElementById("app");
      if (topElement.classList.contains("guide-collapsed")) {
        topElement.classList.toggle("guide-collapsed");
      }
      if (!topElement.classList.contains("activity-collapsed")) {
        topElement.classList.toggle("activity-collapsed");
      }

      this.$store.commit('storeHelpCenterState', true);
      this.$store.commit('storeActiveGuideScope', object);
    },
    // Tasks and guides ask for a tab by name
    handleUpdateTab(response) {
      this.openTab(response === 'eu-gdpr' ? 'gdpr' : response);
    },
    updateProcess() {
      this.loadContent();
      // Activities added or removed, or the legal basis changed: the row and the hero follow
      if (this.$refs.switcher) {
        this.$refs.switcher.reload();
      }
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    reloadContent(object) {
      // If slug is not changed, just reload content
      if (object.slug == this.process.slug) {
        // Remove last item in breadcrumbs, because it will be added again on load content
        this.breadcrumbItems.pop();
        this.loadContent(true);
      } else {
        this.$store.commit('storeUpdateTasks', true);
        // If slug changed, reload state with new param
        this.$router.push({ name: 'processes.view', params: { slug: object.slug }})
      }
    },
    updateValidation(validation) {
      // Event emmited from validation component, update active validation
      this.process.validation = validation
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-process'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-process'].hide()
    },
    confirmDeleteTP(object) {
      let self = this;
      piincHttp.delete('processes/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();

        let objectP = {
          type: 'processes',
          filters: {}
        }
        self.$store.commit('storeActiveFilters', objectP);
        self.$store.commit('storeUpdateTasks', true);
        self.$store.commit('storeActiveTask', {});
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('processes.delete.success'))
      }, function() {});
    },
    // The process opens on itself, also with one activity, as a third party does
    switcherLoaded(row) {
      this.switcherRow = row;
    },
    loadContent(breadcrumbPush) {
      let self = this;
      piincHttp.get('processes/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.process = response.data;
        self.loadTasks('update_basic_info');
        if (breadcrumbPush) {
          // Push Third party name in breadcrumb array
          self.breadcrumbItems.push({text: self.process.name, active: true});
        }
        if (self.$route.query.tab) {
          self.openTab(self.$route.query.tab);
        }

      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.process.who_am_i,
            model_id: this.process.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    }
  },
  // Dialogs of the old sections get the new look while this page is open
  mounted() {
    v2ModalsOn();
  },
  beforeDestroy() {
    v2ModalsOff();
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('storeTabIndex', '');
    next()
  },
  created () {
    let self = this;
    if (this.getClient && this.getClient.slug) {

      this.loadContent(true);

    }


    if (this.$route.params.tabIndex && this.$route.params.validation) {
      // A validation link: 1 = GDPR, 2 = risk management
      setTimeout(function(){
        self.openTab({ 1: 'gdpr', 2: 'risk-management' }[self.$route.params.tabIndex] || 'basic-info');
      }, 1000);
      setTimeout(function(){
        var element = document.getElementsByClassName(self.$route.params.validation);
        element[0].scrollIntoView(true);
        window.scrollBy(0, -100);
        element[0].classList.add("highlight-validation");

      }, 3000);
    }


    this.loadGuides('update_basic_info');
  }
}
</script>
